import * as v2 from '@/core/services/api/v2';

/**
 * Store de clã, falando com o backend V2.
 *
 * Toda a tradução de forma (nomes em PT do wire, boosts em array etc.) mora em
 * `services/api/v2` — aqui só entra o que os componentes já sabem consumir.
 *
 * Diferenças de contrato em relação ao Strapi que moldaram este módulo:
 * - O débito de gold (criar clã, contribuir) é do SERVIDOR. Nunca deduzimos
 *   localmente: em vez disso recarregamos o estado do jogador ou aplicamos o
 *   saldo que a resposta devolve.
 * - Não existe fila de aprovação: entrar num clã aberto é imediato, e clã
 *   fechado só por convite. `approveMember`/`rejectMember` viraram no-ops.
 * - O convite é por USERNAME, não por playerId.
 * - Os custos de boost já vêm nos detalhes do clã (`boostCosts`) — as actions
 *   de custo não fazem mais HTTP.
 */

/** A ordem de leitura do erro é a do contrato do V2; o resto é fallback. */
function mensagemDeErro(error, padrao) {
  return (
    error.response?.data?.message ||
    error.response?.data?.error?.message ||
    error.message ||
    padrao
  );
}

const state = {
  clanDetails: null,
  openClans: [],
  loading: false,
  error: null,
  boostUpgradeCost: {},
  boostActivationCost: {},
  // Convites pendentes do jogador — o getter `pendingInvites` sempre leu daqui,
  // mas o campo (e a action que o preenche) nunca tinham existido.
  pendingInvites: [],
  // Chat
  chatMessages: [],
  chatLoading: false,
  chatError: null,
  chatPagination: {
    page: 1,
    pageSize: 50,
    total: 0
  },
};

const mutations = {
  SET_CLAN_DETAILS(state, details) {
    state.clanDetails = details;
  },
  SET_OPEN_CLANS(state, clans) {
    state.openClans = clans;
  },
  SET_LOADING(state, isLoading) {
    state.loading = isLoading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_PENDING_INVITES(state, invites) {
    state.pendingInvites = invites;
  },
  SET_BOOST_UPGRADE_COST(state, { boostType, level, cost }) {
    if (!state.boostUpgradeCost[boostType]) {
      state.boostUpgradeCost[boostType] = {};
    }
    state.boostUpgradeCost[boostType][level] = cost;
  },
  SET_BOOST_ACTIVATION_COST(state, { boostType, cost }) {
    state.boostActivationCost[boostType] = cost;
  },
  CLEAR_CLAN_DATA(state) {
    state.clanDetails = null;
  },

  // Chat
  SET_CHAT_MESSAGES(state, messages) {
    state.chatMessages = messages;
  },
  ADD_CHAT_MESSAGE(state, message) {
    // No início: a lista é exibida da mais nova para a mais antiga.
    state.chatMessages.unshift(message);
  },
  REMOVE_CHAT_MESSAGE(state, messageId) {
    state.chatMessages = state.chatMessages.filter(msg => msg.id !== messageId);
  },
  SET_CHAT_LOADING(state, isLoading) {
    state.chatLoading = isLoading;
  },
  SET_CHAT_ERROR(state, error) {
    state.chatError = error;
  },
  SET_CHAT_PAGINATION(state, pagination) {
    state.chatPagination = pagination;
  },
  UPDATE_BOOST_LEVEL(state, { boostType, newLevel }) {
    if (state.clanDetails && state.clanDetails.clan_boost) {
      const levelField = `lvl${boostType.charAt(0).toUpperCase() + boostType.slice(1)}`;
      state.clanDetails.clan_boost[levelField] = newLevel;
    }
  },
  UPDATE_CLAN_BALANCE(state, newBalance) {
    if (state.clanDetails) {
      state.clanDetails.balance = newBalance;
    }
  },
  /** O país que o líder acabou de trocar — a resposta da troca já é o valor, sem recarregar o clã. */
  SET_CLAN_COUNTRY(state, country) {
    if (state.clanDetails) {
      state.clanDetails.country = country;
    }
  }
};

const actions = {
  async fetchPlayerClanDetails({ commit, rootState }) {
    const playerId = rootState.player?.id;

    commit('SET_LOADING', true);
    try {
      // Sem jogador ou sem clã não há o que buscar — o V2 devolveria 404 na
      // rota de detalhes e o 404 viraria um erro falso na tela.
      if (!playerId || !rootState.player.clan) {
        commit('SET_CLAN_DETAILS', null);
        commit('SET_LOADING', false);
        return;
      }

      // O adapter já entrega o shape exato de ClanDetails.vue, incluindo
      // `waiting_members: []` (o V2 não tem fila de aprovação) e `boostCosts`.
      const detalhes = await v2.detalhesDoCla(playerId);
      commit('SET_CLAN_DETAILS', detalhes);
      commit('SET_LOADING', false);
    } catch (error) {
      console.error('Error fetching player clan details:', error);
      commit('SET_ERROR', mensagemDeErro(error, 'Failed to fetch clan details'));
      commit('SET_CLAN_DETAILS', null);
      commit('SET_LOADING', false);
    }
  },

  async fetchOpenClans({ commit }) {
    commit('SET_LOADING', true);
    try {
      // Lista paginada por cursor no V2; a primeira página basta para o modal.
      const resposta = await v2.clas();
      commit('SET_OPEN_CLANS', resposta.itens);
      commit('SET_LOADING', false);
    } catch (error) {
      console.error('Error fetching open clans:', error);
      commit('SET_ERROR', mensagemDeErro(error, 'Failed to fetch open clans'));
      commit('SET_LOADING', false);
    }
  },

  /**
   * 7.4c — o tipo É a política de entrada (D22).
   *
   * O comentário anterior dizia que `releasedForAll` "era do Strapi e não existe no contrato de
   * criação do V2: o clã nasce aberto (tipo controla corporation/cooperative, não visibilidade)".
   * A primeira metade estava certa; **a segunda estava errada, e era um bug de verdade**: o
   * `CreateClanModal` oferecia a escolha de "aberto / só por convite" e este código a descartava
   * antes de chamar a API. O jogador escolhia "só por convite", criava o clã, e recebia um clã
   * aberto — porque a coluna nasce `true` no banco.
   *
   * A resolução do D22 é unificar: **`cooperative` é aberto, `corporation` é fechado**, e
   * `releasedForAll` passa a ser derivado do tipo **no servidor** (`clan.service.ts`), que é onde
   * uma regra dessas tem que morar — derivá-la aqui deixaria a mesma decisão em dois lugares, que
   * é exatamente o problema que estamos consertando.
   *
   * O contrato de criação continua sem `releasedForAll`, e agora isso está certo: não há mais o
   * que enviar.
   */
  async createClan({ commit, dispatch, rootState }, { name, description, type, country }) {
    commit('SET_LOADING', true);
    try {
      const playerId = rootState.player?.id;
      const data = await v2.criarCla(playerId, {
        nome: name,
        descricao: description,
        tipo: type,
        pais: country,
      });

      // Os 10.000 de gold são cobrados no servidor — nada de débito local;
      // recarregar o estado traz o gold certo e o vínculo com o clã novo.
      await dispatch('player/loadPlayerState', playerId, { root: true });
      await dispatch('fetchPlayerClanDetails');

      commit('SET_LOADING', false);
      return { success: true, data };
    } catch (error) {
      console.error('Error creating clan:', error);
      const mensagem = mensagemDeErro(error, 'Failed to create clan');
      commit('SET_ERROR', mensagem);
      commit('SET_LOADING', false);
      return { success: false, error: mensagem };
    }
  },

  async joinClan({ commit, dispatch, rootState }, clanId) {
    commit('SET_LOADING', true);
    try {
      const playerId = rootState.player?.id;
      await v2.entrarNoCla(playerId, clanId);

      // O V2 devolve o clã cru, não `{success}` — o contrato com a tela é
      // fabricado aqui. O loadPlayerState traz `player.clan` atualizado.
      await dispatch('player/loadPlayerState', playerId, { root: true });
      await dispatch('fetchPlayerClanDetails');

      commit('SET_LOADING', false);
      return { success: true };
    } catch (error) {
      console.error('Error joining clan:', error);
      const mensagem = mensagemDeErro(error, 'Failed to join clan');
      commit('SET_ERROR', mensagem);
      commit('SET_LOADING', false);
      return { success: false, error: mensagem };
    }
  },

  // O V2 não tem fila de espera: entrar num clã aberto é imediato e clã
  // fechado só aceita por convite. A rota de "pedir para entrar" não existe;
  // mantida como no-op para a tela antiga não quebrar enquanto o botão existir.
  async requestToJoinClan() {
    return { success: false, error: 'Sem fila de aprovação no V2' };
  },

  async leaveClan({ commit, dispatch, rootState }) {
    commit('SET_LOADING', true);
    try {
      const playerId = rootState.player?.id;
      // 204 sem corpo — o `{success:true}` é fabricado.
      await v2.sairDoCla(playerId);
      commit('CLEAR_CLAN_DATA');
      // Sincroniza `player.clan = null`; sem isso o próximo
      // fetchPlayerClanDetails acharia que ainda há clã e tomaria 404.
      await dispatch('player/loadPlayerState', playerId, { root: true });
      commit('SET_LOADING', false);
      return { success: true };
    } catch (error) {
      console.error('Error leaving clan:', error);
      // 409 = líder não sai: precisa transferir a liderança ou dissolver.
      const mensagem = mensagemDeErro(error, 'Failed to leave clan');
      commit('SET_ERROR', mensagem);
      commit('SET_LOADING', false);
      return { success: false, error: mensagem };
    }
  },

  async contributeGold({ commit, dispatch, rootState }, { amount }) {
    if (!rootState.player.clan) {
      return { success: false, error: 'Player is not in a clan' };
    }

    commit('SET_LOADING', true);
    try {
      const playerId = rootState.player?.id;
      // Quem valida saldo e debita é o servidor; a resposta traz o gold
      // restante do jogador — é ele que entra no estado, não uma subtração local.
      const resultado = await v2.contribuirParaOCla(playerId, amount);
      commit('player/SET_GOLD', resultado.gold, { root: true });

      // O bilhete de "contribuição feita", com as moedas de clã que o servidor creditou.
      dispatch(
        'avisos/avisar',
        {
          chave: 'clan.aviso.contribuiu',
          parametros: { coins: resultado.clanCoins ?? 0 },
          icone: 'clan-bau',
        },
        { root: true },
      );

      await dispatch('fetchPlayerClanDetails');
      // Recarrega o jogador: as moedas de clã creditadas pelo servidor entram no baú
      // (`player.clanCoins`) — só o SET_GOLD deixava o saldo de moedas velho na tela.
      await dispatch('player/loadPlayerState', playerId, { root: true });

      commit('SET_LOADING', false);
      return { success: true };
    } catch (error) {
      console.error('Error contributing gold:', error);
      const mensagem = mensagemDeErro(error, 'Failed to contribute gold');
      commit('SET_ERROR', mensagem);
      commit('SET_LOADING', false);
      return { success: false, error: mensagem };
    }
  },

  // O V2 não tem fila de aprovação — `waiting_members` chega sempre vazio dos
  // detalhes, então a tela nem mostra os botões. Os no-ops ficam só para o
  // caso de algum caminho antigo ainda despachar.
  async approveMember() {
    return { success: false, error: 'Sem fila de aprovação no V2' };
  },

  async rejectMember() {
    return { success: false, error: 'Sem fila de aprovação no V2' };
  },

  // `playerId` no payload é o ALVO da expulsão (nome herdado da tela);
  // quem expulsa é o líder autenticado, identificado pelo rootState.
  async kickMember({ commit, dispatch, rootState }, { playerId }) {
    commit('SET_LOADING', true);
    try {
      const meuPlayerId = rootState.player?.id;
      await v2.expulsarDoCla(meuPlayerId, playerId);

      await dispatch('fetchPlayerClanDetails');

      commit('SET_LOADING', false);
      return { success: true };
    } catch (error) {
      console.error('Error kicking member:', error);
      const mensagem = mensagemDeErro(error, 'Failed to kick member');
      commit('SET_ERROR', mensagem);
      commit('SET_LOADING', false);
      return { success: false, error: mensagem };
    }
  },

  async transferLeadership({ commit, dispatch, rootState }, { newLeaderId }) {
    commit('SET_LOADING', true);
    try {
      const playerId = rootState.player?.id;
      await v2.transferirLideranca(playerId, newLeaderId);

      await dispatch('fetchPlayerClanDetails');

      commit('SET_LOADING', false);
      return { success: true };
    } catch (error) {
      console.error('Error transferring leadership:', error);
      const mensagem = mensagemDeErro(error, 'Failed to transfer leadership');
      commit('SET_ERROR', mensagem);
      commit('SET_LOADING', false);
      return { success: false, error: mensagem };
    }
  },

  async disbandClan({ commit, dispatch, rootState }) {
    commit('SET_LOADING', true);
    try {
      const playerId = rootState.player?.id;
      await v2.dissolverCla(playerId);
      commit('CLEAR_CLAN_DATA');
      // Como no leaveClan: o vínculo `player.clan` morreu no servidor.
      await dispatch('player/loadPlayerState', playerId, { root: true });
      commit('SET_LOADING', false);
      return { success: true };
    } catch (error) {
      console.error('Error disbanding clan:', error);
      const mensagem = mensagemDeErro(error, 'Failed to disband clan');
      commit('SET_ERROR', mensagem);
      commit('SET_LOADING', false);
      return { success: false, error: mensagem };
    }
  },

  // Mudança de contrato: o Strapi convidava por playerId; o V2 convida por
  // USERNAME — é o que o líder digita, sem precisar descobrir id de ninguém.
  async invitePlayer({ commit, rootState }, { username }) {
    commit('SET_LOADING', true);
    try {
      const playerId = rootState.player?.id;
      await v2.convidarParaOCla(playerId, username);

      commit('SET_LOADING', false);
      return { success: true };
    } catch (error) {
      console.error('Error inviting player:', error);
      const mensagem = mensagemDeErro(error, 'Failed to invite player');
      commit('SET_ERROR', mensagem);
      commit('SET_LOADING', false);
      return { success: false, error: mensagem };
    }
  },

  // Nunca existiu no Strapi (ClanInvites.vue chamava no vazio); no V2 há rota.
  // Devolve a lista além de commitar, porque a tela usa o retorno direto.
  async fetchPendingInvites({ commit, rootState }) {
    try {
      const playerId = rootState.player?.id;
      if (!playerId) {
        commit('SET_PENDING_INVITES', []);
        return [];
      }
      const convites = await v2.convitesDeCla(playerId);
      commit('SET_PENDING_INVITES', convites);
      return convites;
    } catch (error) {
      console.error('Error fetching pending invites:', error);
      commit('SET_ERROR', mensagemDeErro(error, 'Failed to fetch invites'));
      return [];
    }
  },

  async acceptInvite({ commit, dispatch, rootState }, inviteId) {
    commit('SET_LOADING', true);
    try {
      const playerId = rootState.player?.id;
      await v2.aceitarConvite(playerId, inviteId);

      // Aceitar já coloca no clã: renova convites, vínculo do jogador e detalhes.
      await dispatch('fetchPendingInvites');
      await dispatch('player/loadPlayerState', playerId, { root: true });
      await dispatch('fetchPlayerClanDetails');

      commit('SET_LOADING', false);
      return { success: true };
    } catch (error) {
      console.error('Error accepting invite:', error);
      const mensagem = mensagemDeErro(error, 'Failed to accept invite');
      commit('SET_ERROR', mensagem);
      commit('SET_LOADING', false);
      return { success: false, error: mensagem };
    }
  },

  async rejectInvite({ commit, dispatch, rootState }, inviteId) {
    commit('SET_LOADING', true);
    try {
      const playerId = rootState.player?.id;
      await v2.rejeitarConvite(playerId, inviteId);

      await dispatch('fetchPendingInvites');

      commit('SET_LOADING', false);
      return { success: true };
    } catch (error) {
      console.error('Error rejecting invite:', error);
      const mensagem = mensagemDeErro(error, 'Failed to reject invite');
      commit('SET_ERROR', mensagem);
      commit('SET_LOADING', false);
      return { success: false, error: mensagem };
    }
  },

  // Sem HTTP: os custos de boost já chegam em `clanDetails.boostCosts` — o
  // Strapi exigia uma requisição por custo, o V2 manda tudo nos detalhes.
  async fetchBoostUpgradeCost({ commit, state }, { boostType, currentLevel }) {
    const cost = state.clanDetails?.boostCosts?.[boostType]?.upgrade ?? null;
    if (cost === null) return null;

    commit('SET_BOOST_UPGRADE_COST', {
      boostType,
      level: currentLevel,
      cost
    });

    return cost;
  },

  async fetchBoostActivationCost({ commit, state }, { boostType }) {
    const cost = state.clanDetails?.boostCosts?.[boostType]?.activation ?? null;
    if (cost === null) return null;

    commit('SET_BOOST_ACTIVATION_COST', { boostType, cost });

    // A tela lê `.cost` do retorno.
    return { cost };
  },

  // Pago pelo CAIXA do clã, só pelo líder — 409 se o caixa não cobre.
  async upgradeBoost({ commit, dispatch, rootState }, { boostType }) {
    commit('SET_LOADING', true);
    try {
      const playerId = rootState.player?.id;
      // A resposta é o array cru de boosts do clã, já com o nível novo.
      const boosts = await v2.melhorarBoost(playerId, boostType);
      const boost = (boosts || []).find(b => b.tipo === boostType);

      // Os detalhes trazem saldo do caixa, níveis e custos recalculados.
      await dispatch('fetchPlayerClanDetails');

      commit('SET_LOADING', false);
      return { success: true, newLevel: boost?.nivel };
    } catch (error) {
      console.error('Error upgrading boost:', error);
      const mensagem = mensagemDeErro(error, 'Failed to upgrade boost');
      commit('SET_ERROR', mensagem);
      commit('SET_LOADING', false);
      return { success: false, error: mensagem };
    }
  },

  async activateBoost({ commit, dispatch, rootState }, { boostType }) {
    commit('SET_LOADING', true);
    try {
      const playerId = rootState.player?.id;
      await v2.ativarBoost(playerId, boostType);

      await dispatch('fetchPlayerClanDetails');

      commit('SET_LOADING', false);
      return { success: true, boostType };
    } catch (error) {
      console.error('Error activating boost:', error);
      const mensagem = mensagemDeErro(error, 'Failed to activate boost');
      commit('SET_ERROR', mensagem);
      commit('SET_LOADING', false);
      return { success: false, error: mensagem };
    }
  },

  // Chat
  async fetchClanChatMessages({ commit, rootState, state }) {
    if (!rootState.player.clan) {
      return { success: false, error: 'Player is not in a clan' };
    }

    const playerId = rootState.player?.id;
    const pageSize = state.chatPagination.pageSize || 50;

    commit('SET_CHAT_LOADING', true);
    try {
      // O V2 pagina por CURSOR (`proximoCursor`/`temMais`), não por página.
      // A tela só mostra a primeira leva, então a paginação numerada vira uma
      // fachada: page fixa em 1 e total = o que veio.
      const resposta = await v2.chatDoCla(playerId, { limite: pageSize });

      commit('SET_CHAT_MESSAGES', resposta.mensagens);
      commit('SET_CHAT_PAGINATION', {
        page: 1,
        pageSize,
        total: resposta.mensagens.length
      });

      commit('SET_CHAT_LOADING', false);
      return { success: true, data: resposta };
    } catch (error) {
      console.error('Error fetching clan chat messages:', error);
      const mensagem = mensagemDeErro(error, 'Failed to fetch chat messages');
      commit('SET_CHAT_ERROR', mensagem);
      commit('SET_CHAT_LOADING', false);
      return { success: false, error: mensagem };
    }
  },

  async sendClanChatMessage({ commit, rootState }, text) {
    if (!rootState.player.clan || !rootState.player.id) {
      return { success: false, error: 'Player is not in a clan' };
    }

    const playerId = rootState.player.id;

    commit('SET_CHAT_LOADING', true);
    try {
      // O V2 devolve só `{id}`; a mensagem local é montada aqui para aparecer
      // na hora, no mesmo aninhamento que `mensagemDoCla` produz.
      const { id } = await v2.enviarMensagemDoCla(playerId, text);

      const username = rootState.auth?.user?.username || rootState.player?.username || 'You';
      const newMessage = {
        id,
        text,
        createdAt: new Date().toISOString(),
        player: {
          id: playerId,
          username,
          user: { username }
        }
      };

      commit('ADD_CHAT_MESSAGE', newMessage);
      commit('SET_CHAT_LOADING', false);
      return { success: true, data: { id } };
    } catch (error) {
      console.error('Error sending clan chat message:', error);
      const mensagem = mensagemDeErro(error, 'Failed to send message');
      commit('SET_CHAT_ERROR', mensagem);
      commit('SET_CHAT_LOADING', false);
      return { success: false, error: mensagem };
    }
  },

  async deleteClanChatMessage({ commit, dispatch, rootState }, messageId) {
    if (!rootState.player.clan) {
      return { success: false, error: 'Player is not in a clan' };
    }

    commit('SET_CHAT_LOADING', true);
    try {
      const playerId = rootState.player?.id;
      await v2.apagarMensagemDoCla(playerId, messageId);

      // O refetch é a fonte da verdade; o REMOVE local só evita o piscar.
      commit('REMOVE_CHAT_MESSAGE', messageId);
      await dispatch('fetchClanChatMessages');

      commit('SET_CHAT_LOADING', false);
      return { success: true };
    } catch (error) {
      console.error('Error deleting clan chat message:', error);
      const mensagem = mensagemDeErro(error, 'Failed to delete message');
      commit('SET_CHAT_ERROR', mensagem);
      commit('SET_CHAT_LOADING', false);
      return { success: false, error: mensagem };
    }
  }
};

const getters = {
  playerClan: state => state.playerClan,
  clanDetails: state => state.clanDetails,
  openClans: state => state.openClans,
  pendingInvites: state => state.pendingInvites,
  isLoading: state => state.loading,
  error: state => state.error,
  isPlayerInClan: (state, getters, rootState) => {
    // Verificar se há clanDetails ou se o player tem clan no estado do player
    return !!state.clanDetails || !!rootState.player.clan;
  },
  isPlayerClanLeader: (state, getters, rootState) => {
    if (!state.clanDetails || !rootState.player.id) return false;
    return state.clanDetails.leader?.id === rootState.player.id;
  },
  waitingMembers: state => state.clanDetails?.waiting_members || [],
  clanMembers: state => state.clanDetails?.members || [],
  clanLeader: state => state.clanDetails?.leader || null,
  clanBalance: state => state.clanDetails?.balance || 0,
  clanBoosts: state => state.clanDetails?.boosts || null,
  clanType: state => state.clanDetails?.type || null,
  clanName: state => state.clanDetails?.name || null,
  clanDescription: state => state.clanDetails?.description || null,
  getBoostUpgradeCost: state => (boostType, level) => {
    return state.boostUpgradeCost[boostType]?.[level] || null;
  },
  getBoostActivationCost: state => (boostType) => {
    return state.boostActivationCost[boostType] || null;
  },

  // Chat
  chatMessages: state => state.chatMessages,
  isChatLoading: state => state.chatLoading,
  chatError: state => state.chatError,
  chatPagination: state => state.chatPagination
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
